import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { useMatch } from 'react-router-dom';
import NavLink from './LegacySiteNavLink';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import {
    userHasAccess,
    userHasMultipleClients,
    usersCurrentClientId,
    userIsDev,
} from '../util/hasAccess';

/* @jsxImportSource @emotion/react */
import formatDate from 'date-fns/format';
import config from 'app/config';
import useExpandedMenu from 'app/common/useExpandedMenu';

import TextInput from 'app/components/form/TextInput';

import SiteNavLinkBuilder from 'app/common/SiteNavLinkBuilder';
import baseCss from 'app/common/arctechv2/baseCss';

import { containerCss } from './LegacySiteNavigation.style';
import SiteNavItems from './LegacySiteNavItems';
import ArtechIcon from './svgIcon/ArtechIcon';
import { colors } from './../common/arctechv2/theme';

const SiteNavigation = ({ location, siteNavOpen, setSiteNavOpen }) => {
    const [expandedMenu, setExpandedMenu] = useExpandedMenu();

    const routingGroups = useSelector((state) => state.ROUTING_GROUP_LIST);
    const auth = useSelector((state) => state.auth);
    const clients = useSelector((state) => state.CLIENT_LIST);
    const notifications = useSelector(
        (state) => state.dashboardActivity
    )?.activity;
    const showNewMenu = true;
    // const setShowNewMenu = (v) =>
    // setExpandedMenu({ ...expandedMenu, enabled: true, isDirty: true });
    const [siteNavItems, setSiteNavItems] = useState([]);

    const handleNavScroll = () => {
        const _expandedMenu =
            JSON.parse(window?.localStorage?.getItem('expandedMenu')) ??
            expandedMenu;
        setExpandedMenu({
            ..._expandedMenu,
            scroll: sideNavRef.current.scrollTop,
        });
    };

    const sideNavRef = useRef(null);
    let debounceTimeout = null;
    useEffect(() => {
        if (sideNavRef.current) {
            if (window.setSideNavScrollTop) {
                setTimeout(() => {
                    sideNavRef.current.scrollTo({
                        top: window.setSideNavScrollTop,
                    });
                    delete window.setSideNavScrollTop;
                }, 50);
            }

            function onScroll() {
                if (debounceTimeout) clearTimeout(debounceTimeout);
                debounceTimeout = setTimeout(() => {
                    handleNavScroll();
                }, 500);
            }
            sideNavRef.current.addEventListener('scroll', onScroll);

            return () => {
                sideNavRef?.current?.removeEventListener('scroll', onScroll);
            };
        }
    }, [sideNavRef.current]);

    const match = useMatch('/:module/:clientId');

    useEffect(() => {
        const siteNavItemsNew = SiteNavItems(
            showNav,
            auth,
            config,
            notifications,
            clients,
            canViewZones,
            currentClient
        );

        setSiteNavItems(siteNavItemsNew);
    }, [auth.user, clients, notifications]);

    let currentClientId = usersCurrentClientId(
        auth.user,
        match?.params.clientId ? parseInt(match?.params.clientId) : undefined
    );

    if (isNaN(currentClientId)) {
        currentClientId = -1;
    }

    const currentClient = clients.items?.find((client) => {
        return client.id === currentClientId;
    });

    const showNav = true;

    let showDeliveries = false;
    if (match && match.params && match.params.module === 'cd') {
        showDeliveries =
            location.pathname.indexOf('rg') > -1 ||
            location.pathname.indexOf('dashboard') > -1;
    }

    let logoPath = currentClient?.logoURI || '';
    if (logoPath.substr(0, 1) === '/') {
        logoPath = `https://routing.homedelivery.com.au/${logoPath}`;
    } else if (logoPath.substr(0, 4) !== 'http') {
        logoPath = `${config.s3ImageBase}/${logoPath}`;
    }

    if (currentClientId === -1) {
        logoPath = '/assets/hds-logo-new.svg';
    }

    let canViewZones =
        clients.items?.filter((client) => {
            return client.configuration?.allowViewingZones === true;
        }).length > 0;

    const sitenavLinkBuilder = new SiteNavLinkBuilder(
        auth.user.role,
        currentClientId,
        formatDate(new Date(), 'yyyy-MM-dd'),
        userHasMultipleClients(auth.user),
        true
    );

    // eslint-disable-next-line
    const DeliveriesByStateLink = ({ showHeading = true }) => (
        <div
            className={classNames('submenu deliveries', {
                hide: !showDeliveries,
            })}
        >
            {showHeading && <h5>Deliveries</h5>}
            {userHasAccess(auth.user.role, 'runs') &&
                routingGroups.items?.map((aust) => {
                    return (
                        <NavLink
                            key={aust.id}
                            to={`/cd/${auth.user.role === 'admin' ? currentClientId + '/' : ''}d/rg/${aust.id}`}
                            className="state-link"
                        >
                            <button
                                data-notifications="0"
                                className="nav-button"
                            >
                                <i className="fas fa-map-marker-alt"></i>
                                <div>{aust.name}</div>
                            </button>
                        </NavLink>
                    );
                })}
        </div>
    );

    const checkCondition = (condition) => {
        if (typeof condition == 'undefined' || condition === null) {
            return true;
        } else if (condition !== false) {
            return true;
        } else if (typeof condition === 'function') {
            if (condition()) {
                return true;
            }
        }

        return false;
    };

    const toggleExpandedSection = (index) => {
        const newExpandedMenuSections = expandedMenu.indexes?.includes(index)
            ? expandedMenu.indexes?.filter((i) => i !== index)
            : [...expandedMenu.indexes, index];
        setExpandedMenu({
            ...expandedMenu,
            indexes: newExpandedMenuSections,
            isDirty: true,
        });
    };

    const isExpanded = (sectionIndex) => {
        return expandedMenu.indexes?.includes(sectionIndex);
    };

    const MenuBuilder = ({
        navItemsAlt,
        collapsible,
        expandedMenuSections,
        handleToggleExpand,
        searchKey,
    }) => {
        return (
            <>
                {navItemsAlt.map((item, index) => {
                    const sectionIsCollapsible =
                        item.collapsible ??
                        (collapsible && item.items.length > 1);
                    let sectionIsExpanded =
                        !collapsible ||
                        (!sectionIsCollapsible && item.items.length < 2) ||
                        (sectionIsCollapsible &&
                            expandedMenuSections?.includes(index));

                    let childLinks = item.items.filter(
                        (itemSub) => itemSub.type !== 'heading'
                    );

                    if (searchKey) {
                        sectionIsExpanded = true;
                        childLinks = childLinks.filter((itemSub) =>
                            sitenavLinkBuilder
                                .getLabel(itemSub.component, 'new')
                                ?.trim()
                                .toLowerCase()
                                ?.includes(searchKey?.trim().toLowerCase())
                        );
                    }

                    const hasChildLink = childLinks
                        .map((itemSub) => checkCondition(itemSub.condition))
                        .find((itemSub) => itemSub === true);

                    const render = (
                        <div
                            className={`submenu ${item.classNames} ${hasChildLink ? 'has-child' : 'no-child'}`}
                            key={index}
                        >
                            {item.items.map((itemSub, indexSub) => {
                                if (itemSub.type === 'heading') {
                                    if (sectionIsCollapsible) {
                                        return (
                                            <a
                                                key={indexSub}
                                                className={`nav-button nav-button--collapse-toggle ${sectionIsExpanded ? 'expanded' : 'collapsed'}`}
                                            >
                                                <button
                                                    onClick={() => {
                                                        handleToggleExpand(
                                                            index
                                                        );
                                                    }}
                                                >
                                                    <h5>
                                                        {itemSub.text}
                                                        <div className="collapse-toggle">
                                                            {isExpanded(
                                                                index
                                                            ) ? (
                                                                <i className="fas fa-caret-up"></i>
                                                            ) : (
                                                                <i className="fas fa-caret-down"></i>
                                                            )}
                                                        </div>
                                                    </h5>
                                                </button>
                                            </a>
                                        );
                                    } else {
                                        return (
                                            <h5 key={indexSub}>
                                                {itemSub.text}
                                            </h5>
                                        );
                                    }
                                }
                                if (
                                    itemSub.type === 'component' &&
                                    sectionIsExpanded
                                ) {
                                    if (checkCondition(itemSub.condition)) {
                                        switch (itemSub.component) {
                                            case 'DeliveriesByStateLink':
                                                return (
                                                    <DeliveriesByStateLink
                                                        key={indexSub}
                                                    />
                                                );
                                            case 'DeliveriesByStateNoHeadingLink':
                                                return (
                                                    <DeliveriesByStateLink
                                                        key={indexSub}
                                                        showHeading={false}
                                                    />
                                                );
                                        }
                                    }
                                }
                                if (
                                    (!sectionIsCollapsible ||
                                        sectionIsExpanded) &&
                                    checkCondition(itemSub.condition)
                                ) {
                                    if (
                                        sitenavLinkBuilder
                                            .getLabel(
                                                itemSub.component,
                                                itemSub.type === 'linkNew'
                                                    ? 'new'
                                                    : null
                                            )
                                            ?.toLowerCase()
                                            .includes(
                                                searchKey?.toLowerCase().trim()
                                            )
                                    ) {
                                        return sitenavLinkBuilder.generateLink(
                                            itemSub.component,
                                            itemSub.notifications,
                                            itemSub.type === 'linkNew',
                                            itemSub.type === 'linkNew'
                                        );
                                    }
                                }
                            })}
                        </div>
                    );
                    if (checkCondition(item.condition)) {
                        return render;
                    }
                })}
            </>
        );
    };
    MenuBuilder.displayName = 'MenuBuilder';
    MenuBuilder.propTypes = {
        navItemsAlt: PropTypes.array,
        collapsible: PropTypes.bool,
        expandedMenuSections: PropTypes.array,
        handleToggleExpand: PropTypes.func,
        searchKey: PropTypes.string,
    };

    const [searchKey, setSearchKey] = useState('');

    return (
        <aside
            css={[baseCss, containerCss]}
            className={siteNavOpen ? '' : 'is-closed'}
        >
            <button
                type="button"
                className="sidemenu-trigger js-sidemenu-trigger"
                onClick={() => setSiteNavOpen(!siteNavOpen)}
            >
                <i className="fa-solid fa-chevron-left"></i>
            </button>
            <div className="wrap" ref={sideNavRef}>
                {showNav && (
                    <div className="logo">
                        <img
                            className="logo-img"
                            src={logoPath}
                            alt={currentClient?.displayName}
                        />
                        <div
                            css={{
                                display: 'flex',
                                paddingTop: '9px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: '15px',
                                alignSelf: 'stretch',
                                marginTop: '10px',
                            }}
                        >
                            <div
                                css={{
                                    color: colors.gray400,
                                    fontSize: '10px',
                                    fontWeight: 500,
                                }}
                            >
                                POWERED BY
                            </div>
                            <ArtechIcon />
                        </div>
                    </div>
                )}

                <div className="d-flex text-center justify-content-center align-items-center text-white "></div>

                <div className="menu-search my-4">
                    <TextInput
                        placeholder="Search Menu"
                        value={searchKey}
                        onChange={(e) => {
                            setSearchKey(e.target.value);
                        }}
                    />
                    {searchKey && (
                        <button
                            className="text-field-clear"
                            onClick={() => {
                                setSearchKey('');
                            }}
                        >
                            <i className=" fa-regular fa-circle-xmark"></i>
                        </button>
                    )}
                </div>
                <MenuBuilder
                    searchKey={searchKey}
                    navItemsAlt={siteNavItems}
                    collapsible={showNewMenu}
                    expandedMenuSections={expandedMenu.indexes}
                    handleToggleExpand={toggleExpandedSection}
                />

                <div className="submenu stats">
                    <NavLink to="/auth/logout">
                        <button data-notifications="0" className="nav-button">
                            <i className="fa-solid fa-user" />
                            <span>Logout</span>
                        </button>
                    </NavLink>
                </div>

                {userIsDev(auth.user) && showNav && (
                    <div className="submenu stats">
                        <h5>Queue Info</h5>
                        <div className="stats">
                            <div className="text">
                                <span>Queued</span>
                                <span
                                    className="percents"
                                    data-percent={notifications?.queueLength}
                                ></span>
                            </div>
                        </div>
                        <div className="stats">
                            <div className="text">
                                <span>Servers</span>
                                <span
                                    className="percents"
                                    data-percent={notifications?.queueServers}
                                ></span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </aside>
    );
};
SiteNavigation.displayName = 'SiteNavigation';
SiteNavigation.propTypes = {
    location: PropTypes.object,
    siteNavOpen: PropTypes.bool,
    setSiteNavOpen: PropTypes.func,
};

export default SiteNavigation;
